import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  UserOutlined,
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  LinkedinOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import ConsultantRegister from '../../components/auth/ConsultantRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const ConsultantManagement = () => {
  const [consultants, setConsultants] = useState([]);
  const [filteredConsultants, setFilteredConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch consultants from API
  const loadConsultants = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/consultants');
      if (response.data && response.data.success) {
        setConsultants(response.data.consultants);
        setFilteredConsultants(response.data.consultants);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch consultants');
      }
    } catch (error) {
      console.error('Error loading consultants:', error);
      toast.error('Error loading consultants. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConsultants();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = consultants.filter((consultant) => 
      consultant.fullName?.toLowerCase().includes(value) ||
      consultant.email?.toLowerCase().includes(value) ||
      consultant.phone?.includes(value) ||
      consultant.expertise?.toLowerCase().includes(value) ||
      consultant.consultingFields?.some(field => field.toLowerCase().includes(value))
    );
    setFilteredConsultants(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredConsultants(consultants);
  };

  // Toggle verification status
  const handleToggleVerification = async (consultantId, currentStatus) => {
    Modal.confirm({
      title: `${currentStatus === 'verified' ? 'Unverify' : 'Verify'} Consultant`,
      content: `Are you sure you want to ${currentStatus === 'verified' ? 'unverify' : 'verify'} this consultant?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/consultants/${consultantId}/verify`);
          
          if (response.data && response.data.success) {
            toast.success(`Consultant ${currentStatus === 'verified' ? 'unverified' : 'verified'} successfully`);
            loadConsultants();
          }
        } catch (error) {
          console.error('Error toggling verification:', error);
          toast.error('Failed to update verification status');
        }
      }
    });
  };

  // Approve / Reject registration status
  const handleApprove = (consultantId) => {
    Modal.confirm({
      title: 'Approve Consultant Registration',
      content: 'Are you sure you want to approve this consultant?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/consultants/${consultantId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Consultant approved successfully');
            loadConsultants();
          }
        } catch (error) {
          toast.error('Failed to approve consultant');
        }
      }
    });
  };

  const handleRevert = (consultantId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/consultants/${consultantId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Consultant reverted to pending');
            loadConsultants();
          }
        } catch (error) {
          toast.error('Failed to revert consultant');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (consultant) => {
    setRejectTarget(consultant);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/consultants/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Consultant rejected');
        setRejectModalOpen(false);
        loadConsultants();
      }
    } catch (error) {
      toast.error('Failed to reject consultant');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingConsultant(null);
    setIsModalOpen(true);
  };

  const handleEdit = (consultant) => {
    setEditingConsultant(consultant);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingConsultant(null), 300);
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadConsultants();
  };

  // Delete Consultant
  const handleDelete = (consultantId) => {
    Modal.confirm({
      title: 'Delete Consultant',
      content: 'Are you sure you want to delete this consultant? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/consultants/${consultantId}`);
          
          if (response.data && response.data.success) {
            toast.success('Consultant deleted successfully');
            loadConsultants();
          } else {
            throw new Error(response.data?.message || 'Failed to delete consultant');
          }
        } catch (error) {
          toast.error('Failed to delete consultant.');
        }
      }
    });
  };

  // Helper function for status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Statistics Calculation
  const stats = {
    total: consultants.length,
    approved: consultants.filter(c => c.status === 'approved').length,
    verified: consultants.filter(c => c.verificationStatus === 'verified').length,
    pending: consultants.filter(c => c.status === 'pending').length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <UserOutlined /> },
    approved: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    verified: { bg: '#fff7e6', color: '#fa8c16', icon: <SafetyCertificateOutlined /> },
    pending: { bg: '#fff1f0', color: '#ff4d4f', icon: <ClockCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>#</span>,
      key: 'index',
      width: 60,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Consultant Name</span>,
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text, record) => {
        // Construct proper image URL
        let imageUrl = '';
        if (record.profilePicture) {
          if (record.profilePicture.startsWith('http')) {
            imageUrl = record.profilePicture;
          } else if (record.profilePicture.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${record.profilePicture}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${record.profilePicture}`;
          }
        }
        
        return (
          <Space>
            {record.profilePicture ? (
              <Avatar src={imageUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40} icon={<UserOutlined />} />
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block', wordWrap: 'break-word', maxWidth: '200px' }}>{text}</Text>
              <Text type="secondary" style={{ fontSize:"12px" }}>{record.expertise || 'N/A'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Experience</span>,
      dataIndex: 'yearsOfExperience',
      key: 'yearsOfExperience',
      render: (years) => (
        <Text style={{ fontSize:"14px", fontWeight: 500 }}>
          {years} {years === 1 ? 'year' : 'years'}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Consulting Fields</span>,
      dataIndex: 'consultingFields',
      key: 'consultingFields',
      render: (fields) => (
        <Space wrap size={4}>
          {fields && fields.length > 0 ? (
            fields.slice(0, 2).map((field, idx) => (
              <Tag key={idx} color="blue">{field}</Tag>
            ))
          ) : (
            <Text type="secondary">N/A</Text>
          )}
          {fields && fields.length > 2 && (
            <Tag>+{fields.length - 2} more</Tag>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => (
        <Space direction="vertical" size={4}>
          <Tag color={getStatusColor(record.status)} icon={record.status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {record.status}
          </Tag>
          <Tag color={record.verificationStatus === 'verified' ? 'green' : 'orange'} icon={record.verificationStatus === 'verified' ? <SafetyCertificateOutlined /> : <ClockCircleOutlined />}>
            {record.verificationStatus}
          </Tag>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize:"13px", color: '#666' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 150,
      render: (record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record._id)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title={record.verificationStatus === 'verified' ? "Unverify" : "Verify"}>
            <Button
              type="link"
              icon={<SafetyCertificateOutlined />}
              onClick={() => handleToggleVerification(record._id, record.verificationStatus)}
              style={{ color: record.verificationStatus === 'verified' ? '#52c41a' : '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: "'Rubik', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#333', fontFamily: "'Rubik', sans-serif" }}>
          Consultant Management
        </Title>
        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif" }}>
          Manage all consultants in the ecosystem
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.total.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.total.color }}>{cardStyles.total.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.total.color, display: 'block' }}>
                  {stats.total}
                </Text>
                <Text type="secondary">Total Consultants</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.approved.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.approved.color }}>{cardStyles.approved.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.approved.color, display: 'block' }}>
                  {stats.approved}
                </Text>
                <Text type="secondary">Approved</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.verified.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.verified.color }}>{cardStyles.verified.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.verified.color, display: 'block' }}>
                  {stats.verified}
                </Text>
                <Text type="secondary">Verified</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.pending.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.pending.color }}>{cardStyles.pending.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.pending.color, display: 'block' }}>
                  {stats.pending}
                </Text>
                <Text type="secondary">Pending</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search consultants by name, email, phone, expertise, or field"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredConsultants.length} of {consultants.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Consultant
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredConsultants}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{ 
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        scroll={{ x: 'max-content' }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingConsultant ? 'Edit Consultant' : 'Add New Consultant'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <ConsultantRegister
          editingConsultant={editingConsultant}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Consultant Registration"
        entityLabel={rejectTarget?.fullName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default ConsultantManagement;
